// Building blocks that make a section editable in the admin editor.
// On the public site (no `edit` in context) they render exactly what the
// section rendered before, with no extra markup.
import { ElementType, KeyboardEvent, ReactNode, ClipboardEvent, FocusEvent } from 'react';
import { FaArrowLeft, FaArrowRight, FaCamera, FaPen, FaPlus, FaTrash } from 'react-icons/fa';
import { useSite } from '../content/context';
import './edit.css';

interface EditableTextProps {
  value?: string;
  onChange: (value: string) => void;
  /** Accessible name, e.g. "Band member name". */
  label: string;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  as?: ElementType;
  /** Return an error message to reject the edit (it is reverted). */
  validate?: (value: string) => string | undefined;
}

const normalize = (text: string, multiline?: boolean) =>
  multiline ? text.replace(/\r/g, '').replace(/\n{3,}/g, '\n\n').trim() : text.replace(/\s+/g, ' ').trim();

/** Text you click and type over, right on the page. */
export function EditableText({ value = '', onChange, label, multiline, placeholder, className, as: Tag = 'span', validate }: EditableTextProps) {
  const { edit } = useSite();
  if (!edit) return <Tag className={className}>{value}</Tag>;

  const commit = (e: FocusEvent<HTMLElement>) => {
    const next = normalize(e.currentTarget.innerText, multiline);
    if (next === value) return;
    const error = validate?.(next);
    if (error) {
      window.alert(error);
      e.currentTarget.innerText = value;
      return;
    }
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.currentTarget.innerText = value;
      e.currentTarget.blur();
    } else if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  // Paste as plain text so formatting from other apps doesn't come along.
  const onPaste = (e: ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, multiline ? text : text.replace(/\s+/g, ' '));
  };

  return (
    <Tag
      // Remount when the value changes (undo, discard) so the DOM matches it.
      key={value}
      className={`${className ?? ''} af-editable${multiline ? ' af-prewrap' : ''}`}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={label}
      aria-multiline={multiline || undefined}
      tabIndex={0}
      data-placeholder={placeholder ?? `Add ${label.toLowerCase()}`}
      onBlur={commit}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
    >
      {value}
    </Tag>
  );
}

interface ActionButtonProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  showLabel?: boolean;
  danger?: boolean;
  disabled?: boolean;
}

export const ActionButton = ({ label, icon, onClick, showLabel, danger, disabled }: ActionButtonProps) => (
  <button
    type="button"
    className={`af-btn${showLabel ? '' : ' af-btn-icon'}${danger ? ' af-btn-danger' : ''}`}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    aria-label={label}
    title={label}
    disabled={disabled}
  >
    {icon}
    {showLabel ? <span>{label}</span> : null}
  </button>
);

/** A "Change photo" button pinned to the corner of its (position: relative) parent. */
export function ChangePhotoButton({ onChange, label = 'Change photo', left }: { onChange: (path: string) => void; label?: string; left?: boolean }) {
  const { edit } = useSite();
  if (!edit) return null;
  return (
    <div className={`af-actions${left ? ' af-actions-left' : ''}`}>
      <ActionButton
        label={label}
        icon={<FaCamera />}
        showLabel
        onClick={async () => {
          const path = await edit.chooseImage();
          if (path) onChange(path);
        }}
      />
    </div>
  );
}

interface ItemControlsProps {
  /** e.g. "show", "band member" — used in button labels. */
  noun: string;
  index: number;
  count: number;
  onMove?: (from: number, to: number) => void;
  onRemove?: () => void;
  onEdit?: () => void;
  onChangePhoto?: (path: string) => void;
}

/** Edit / move / delete buttons for one card in a list. */
export function ItemControls({ noun, index, count, onMove, onRemove, onEdit, onChangePhoto }: ItemControlsProps) {
  const { edit } = useSite();
  if (!edit) return null;
  return (
    <div className="af-actions">
      {onChangePhoto ? (
        <ActionButton
          label={`Change ${noun} photo`}
          icon={<FaCamera />}
          onClick={async () => {
            const path = await edit.chooseImage();
            if (path) onChangePhoto(path);
          }}
        />
      ) : null}
      {onEdit ? <ActionButton label={`Edit ${noun} details`} icon={<FaPen />} onClick={onEdit} /> : null}
      {onMove ? (
        <>
          <ActionButton label={`Move ${noun} earlier`} icon={<FaArrowLeft />} onClick={() => onMove(index, index - 1)} disabled={index === 0} />
          <ActionButton label={`Move ${noun} later`} icon={<FaArrowRight />} onClick={() => onMove(index, index + 1)} disabled={index === count - 1} />
        </>
      ) : null}
      {onRemove ? (
        <ActionButton
          label={`Delete ${noun}`}
          icon={<FaTrash />}
          danger
          onClick={() => {
            if (window.confirm(`Delete this ${noun}? You can undo this until you publish.`)) onRemove();
          }}
        />
      ) : null}
    </div>
  );
}

/** A dashed "+ Add …" tile shown at the end of a list in edit mode. */
export function AddTile({ label, onClick }: { label: string; onClick: () => void }) {
  const { edit } = useSite();
  if (!edit) return null;
  return (
    <button type="button" className="af-add-tile" onClick={onClick}>
      <FaPlus size={22} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
